import { _isNil, _notNil } from '@/littledash';
import type {
  FindingsSummary,
  SendAdvisoryCounts,
  SendBundleValidationResult,
  SendReportCounts,
  SendRunMetadata,
} from '@/model/Agent.model';

/**
 * How AI output is labelled wherever the SEND results show it. The model behind it is never named,
 * so the customer does not see it change when the model does.
 */
export const IN_VIVO_AI = 'Benchling In Vivo AI';

/** The wire value marking a finding as AI output. Mapped to {@link IN_VIVO_AI}, never shown as-is. */
const AI_ORIGIN = 'llm';

export const isAiFinding = (finding: { origin?: string | null }): boolean => finding.origin === AI_ORIGIN;

/**
 * Whether a cross-domain finding reports a check that could not be completed — skipped, or unable to
 * decide some comparisons — rather than something it found. The validator says so with a typed
 * `disclosure`; a result stored before it did marks those notices as a check's `info` alone. AI
 * notes are excluded by origin.
 */
export const isIncompleteCheck = (finding: {
  severity: string;
  origin?: string | null;
  disclosure?: string | null;
}): boolean => _notNil(finding.disclosure) || (finding.severity === 'info' && !isAiFinding(finding));

/** How a finding from the validator's own checks is labelled. */
export const VALIDATION_CHECK = 'Validation check';

/** Where a finding came from, as every SEND results view labels it. */
export interface FindingSource {
  key: 'check' | 'ai';
  label: string;
}

/**
 * Anything not marked as AI output is a validation check, so a new rule's findings are labelled
 * without opting in. The server refuses any other origin value.
 */
export const sourceOf = (finding: { origin?: string | null }): FindingSource =>
  isAiFinding(finding) ? { key: 'ai', label: IN_VIVO_AI } : { key: 'check', label: VALIDATION_CHECK };

/**
 * Orders AI notes after the checks' own findings, then by the given comparator. The server delivers
 * them in that order, and re-sorting by severity alone would put an AI warning above a check's note.
 */
export const aiNotesLast =
  <T extends { origin?: string | null }>(then: (a: T, b: T) => number) =>
  (a: T, b: T): number => {
    const byOrigin = Number(isAiFinding(a)) - Number(isAiFinding(b));
    return byOrigin !== 0 ? byOrigin : then(a, b);
  };

/**
 * What part of a result showed against what it found. Read from exact counts, so a finding a limit
 * left out is always counted here even though it is missing from the list.
 */
export interface SendOmission {
  /** How many of the checks' findings the result shows. */
  shown: number;
  /** How many the checks found. */
  found: number;
  /** How many Benchling In Vivo AI notes there were, before any limit. */
  aiNotes: number;
  aiNotesNotShown: number;
}

export const omissionOf = (counts: SendReportCounts, advisory: SendAdvisoryCounts): SendOmission => ({
  shown: counts.displayed_samples,
  found: counts.finding_groups,
  aiNotes: advisory.observations,
  aiNotesNotShown: advisory.omitted_observations,
});

export const findingsNotShown = (omission: SendOmission): number => Math.max(0, omission.found - omission.shown);

/**
 * The cross-domain checks' own omission. The result reports the whole bundle and each file, so the
 * cross-domain part is the difference; null when the result predates those counts.
 */
export const crossDomainOmissionOf = (result: SendBundleValidationResult): SendOmission | null => {
  if (_isNil(result.report)) {
    return null;
  }
  let omission = omissionOf(result.report.summary, result.report.advisory_counts);
  for (const file of result.per_domain) {
    if (_isNil(file.counts) || _isNil(file.advisory)) {
      return null;
    }
    omission = {
      shown: omission.shown - file.counts.displayed_samples,
      found: omission.found - file.counts.finding_groups,
      aiNotes: omission.aiNotes - file.advisory.observations,
      aiNotesNotShown: omission.aiNotesNotShown - file.advisory.omitted_observations,
    };
  }
  return omission;
};

/**
 * The cross-domain checks' own severity totals: the bundle's less every file's, all exact and taken
 * before any limit. The bundle totals include every file, so reading them directly would let a file's
 * blocking error turn the cross-domain banner critical. Null when the result predates those counts.
 */
export const crossDomainSummaryOf = (result: SendBundleValidationResult): FindingsSummary | null => {
  if (_isNil(result.report)) {
    return null;
  }
  return result.per_domain.reduce<FindingsSummary>(
    (tier, file) => ({
      error_count: tier.error_count - file.summary.error_count,
      warning_count: tier.warning_count - file.summary.warning_count,
      info_count: tier.info_count - file.summary.info_count,
      submission_blocking_count: tier.submission_blocking_count - file.summary.submission_blocking_count,
    }),
    { ...result.summary }
  );
};

/** A label telling a reviewer whether Benchling In Vivo AI took part in a run. */
export interface AiUseLabel {
  key: 'with_ai' | 'without_ai';
  text: string;
}

/**
 * Keyed on `llm_used`, not `mode`: an AI-enabled run with nothing to ask reads as checked without AI,
 * and one whose calls were attempted and failed still reads as checked with it. Null when the result
 * predates the record.
 */
export const aiUseLabelOf = (runMetadata?: SendRunMetadata | null): AiUseLabel | null => {
  if (_isNil(runMetadata)) {
    return null;
  }
  return runMetadata.llm_used
    ? { key: 'with_ai', text: `Checked with ${IN_VIVO_AI}` }
    : { key: 'without_ai', text: `Checked without ${IN_VIVO_AI}` };
};
